import { useCallback, useMemo } from "react";
import { Users } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import usePageTitle from "hooks/usePageTitle";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import NewContactButton from "routes/contact/components/NewContactButton";
import ContactsActionButtons from "routes/contact/components/contacts_table/ContactsActionButtons";
import ContactService from "services/Contact";
import { useContactsTable } from "hooks/useContactsTable";
import { omitByNil } from "helpers/tables";

export const CONTACTS_TABLE_CONSTANTS = {
  tableName: "contacts",
  className: "Contact",
  exportTableName: "ContactsTable",
  header: "Contacts",
  headerIcon: Users,
};

const ContactsTableV3 = ({
  entityId = null,
  campaignId = null,
  enableCalling = false,
  tableName = CONTACTS_TABLE_CONSTANTS.tableName,
}) => {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.contactTags.create,
      ACL_RELATIONSHIPS.contactTags.read,
      ACL_RELATIONSHIPS.contactTags.delete,
    ],
    { partialMatch: true }
  );

  const isStandalone = !entityId && !campaignId;
  usePageTitle(isStandalone ? CONTACTS_TABLE_CONSTANTS.header : null);

  const defaultQueryParams = useMemo(
    () =>
      omitByNil({
        entity_id: entityId,
        campaign_id: campaignId,
      }),
    [entityId, campaignId]
  );

  const { filters, columns, customFields, queryKey, invalidateData } =
    useContactsTable({
      enableCalling,
      tableName,
      entityId,
    });

  const fetchContacts = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);

        const response = await ContactService.getContacts({
          ...nextQueryParams,
          ...defaultQueryParams,
        });
        return response;
      } catch (error) {
        console.error("Error fetching contacts:", error);
        throw error;
      }
    },
    [columns, defaultQueryParams]
  );

  const actionButtons = useCallback(
    ({ selectedIds, selectedData, uncheckAll }) => (
      <ContactsActionButtons
        selectedIds={selectedIds}
        selectedData={selectedData}
        uncheckAll={uncheckAll}
        invalidateData={invalidateData}
      />
    ),
    [invalidateData]
  );

  return (
    <BaseTableV3
      columns={columns}
      filters={filters}
      customFields={customFields}
      headerIcon={CONTACTS_TABLE_CONSTANTS.headerIcon}
      header={CONTACTS_TABLE_CONSTANTS.header}
      queryKey={queryKey}
      className={CONTACTS_TABLE_CONSTANTS.className}
      exportTableName={CONTACTS_TABLE_CONSTANTS.exportTableName}
      tagsDropdownParams={{ visible: enableTags }}
      tableName={tableName}
      fetchData={fetchContacts}
      createButton={<NewContactButton entityId={entityId} />}
      actionButtons={actionButtons}
      invalidateData={invalidateData}
      downloadParams={defaultQueryParams}
      enableSearch
      enableSettings
    />
  );
};

export default ContactsTableV3;
